<template>
  <div class="relative min-h-screen min-h-dvh flex flex-col items-center justify-center px-4 sm:px-6 py-6 sm:py-8 bg-[#f0f0f5] text-slate-900 select-none overflow-x-hidden">
    <!-- Language Switcher in top right corner -->
    <div
      class="absolute right-4 sm:right-6 z-20 flex items-center gap-1.5 bg-white px-3 py-2 rounded-full border border-slate-200 shadow-sm"
      :style="{ top: 'max(3rem, calc(env(safe-area-inset-top, 0px) + 0.75rem))' }"
    >
      <GlobeAltIcon class="w-4 h-4 text-indigo-500" />
      <select
        v-model="currentLanguage"
        @change="onLanguageChange"
        class="bg-transparent text-xs font-semibold text-slate-700 border-none outline-none cursor-pointer pr-1"
        :aria-label="$t('login.language')"
      >
        <option value="pt-BR">Português</option>
        <option value="en">English</option>
        <option value="es">Español</option>
        <option value="fr">Français</option>
      </select>
    </div>

    <div class="relative z-10 w-full max-w-sm sm:max-w-md">
      <!-- Card no estilo da tela de login (dev-login.triadmusic.app) -->
      <div class="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/[0.05] p-6 sm:p-8">
        <div class="flex gap-2 mb-3" aria-hidden="true">
          <div class="h-1 w-1/2 rounded-full bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-400"></div>
          <div class="h-1 w-1/2 rounded-full bg-slate-200"></div>
        </div>

        <p class="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 mb-5">
          {{ $t('login.step') }}
        </p>

        <div class="flex items-start justify-between gap-4 mb-3">
          <h1
            class="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight cursor-pointer select-none"
            @click="handleLogoTap"
          >
            {{ $t('login.title') }}
          </h1>
          <img
            src="@/assets/triad-logo.png"
            alt="Triad Music"
            class="w-14 sm:w-16 shrink-0 object-contain cursor-pointer select-none active:scale-95 transition-transform"
            @click="handleLogoTap"
          />
        </div>

        <p class="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 sm:mb-10">
          {{ $t('login.subtitle') }}
        </p>

        <!-- Action Button -->
        <button
          type="button"
          @click="handleLogin"
          :disabled="loading"
          class="w-full py-4 px-6 rounded-3xl bg-gradient-to-r from-blue-500 to-indigo-600 active:scale-[0.98] text-white font-semibold text-base sm:text-lg shadow-lg shadow-indigo-500/25 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-80"
        >
          <span v-if="loading" class="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
          <ArrowRightIcon v-else class="w-5 h-5" />
          <span>{{ loading ? $t('login.connecting') : $t('login.signInButton') }}</span>
        </button>

        <p class="text-xs sm:text-sm text-slate-500 mt-6 sm:mt-8 leading-relaxed">
          {{ $t('login.termsNoticePrefix') }}
          <a href="https://triadmusic.app/terms" target="_blank" rel="noopener noreferrer" @click.prevent="openExternalUrl('https://triadmusic.app/terms')" class="underline underline-offset-2 hover:text-indigo-700 font-medium">
            {{ $t('legal.termsOfService') }}
          </a>
          {{ $t('login.termsNoticeAnd') }}
          <a href="https://triadmusic.app/privacy" target="_blank" rel="noopener noreferrer" @click.prevent="openExternalUrl('https://triadmusic.app/privacy')" class="underline underline-offset-2 hover:text-indigo-700 font-medium">
            {{ $t('legal.privacyPolicy') }}
          </a>.
        </p>
      </div>

      <!-- Versão do App no Rodapé -->
      <p
        v-if="appVersion"
        class="text-xs text-slate-400 mt-6 font-mono text-center w-full select-none cursor-pointer hover:text-slate-600 transition-colors"
        @click="handleLogoTap"
        :title="$t('login.title')"
      >
        v{{ appVersion }}
      </p>
    </div>

    <!-- Fullscreen Video Splash Overlay -->
    <teleport to="body">
      <transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-300 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showSplashVideo"
          class="fixed inset-0 z-[999999] flex items-center justify-center bg-[#E7E6E2]"
          @click="closeSplashVideo"
        >
          <video
            ref="splashVideoRef"
            src="/splash_video.mp4"
            playsinline
            class="w-full h-full object-contain"
            @ended="closeSplashVideo"
            @error="closeSplashVideo"
          ></video>

          <button
            type="button"
            class="absolute right-4 z-10 p-2.5 rounded-full bg-black/20 hover:bg-black/40 text-slate-800 transition-colors cursor-pointer"
            :style="{ top: 'max(2rem, calc(env(safe-area-inset-top, 0px) + 0.75rem))' }"
            @click.stop="closeSplashVideo"
            aria-label="Fechar vídeo"
          >
            <XMarkIcon class="w-6 h-6 text-slate-800" />
          </button>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { setLanguage } from '@/i18n';
import { GlobeAltIcon, XMarkIcon, ArrowRightIcon } from '@heroicons/vue/24/outline';
import userStore from '@/store/user.store';
import { openAuthSession, withTimeout, openExternalUrl } from '@/utils/capacitor';
import { appEvents } from '@/store/app.events';
import { getCurrentAppVersion } from '@/service/appUpdate.service';
import { clearAllUserCaches } from '@/utils/authCleanup';

const router = useRouter();
const { locale } = useI18n();
const currentLanguage = ref(locale.value);

const onLanguageChange = () => {
  setLanguage(currentLanguage.value);
};
const loading = ref(false);
const appVersion = ref('');

// Toque 4 vezes seguidas sem pausa para abrir o vídeo splash de abertura
const tapCount = ref(0);
let tapTimer = null;
const TAP_MAX_INTERVAL_MS = 600;

const showSplashVideo = ref(false);
const splashVideoRef = ref(null);
let splashTimeout = null;

const openSplashVideo = async () => {
  showSplashVideo.value = true;
  await nextTick();
  if (splashVideoRef.value) {
    splashVideoRef.value.currentTime = 0;
    try {
      await splashVideoRef.value.play();
    } catch (e) {
      // Tenta reproduzir sem som caso a política do navegador/dispositivo bloqueie autoplay com áudio
      if (splashVideoRef.value) {
        splashVideoRef.value.muted = true;
        splashVideoRef.value.play().catch(() => closeSplashVideo());
      }
    }
  }
  if (splashTimeout) clearTimeout(splashTimeout);
  splashTimeout = setTimeout(() => {
    closeSplashVideo();
  }, 12000);
};

const closeSplashVideo = () => {
  if (splashTimeout) {
    clearTimeout(splashTimeout);
    splashTimeout = null;
  }
  if (splashVideoRef.value) {
    try {
      splashVideoRef.value.pause();
    } catch (e) {
      // ignore pause error on closing video
    }
  }
  showSplashVideo.value = false;
};

const handleLogoTap = () => {
  tapCount.value += 1;
  if (tapTimer) clearTimeout(tapTimer);

  if (tapCount.value >= 4) {
    tapCount.value = 0;
    openSplashVideo();
    return;
  }

  tapTimer = setTimeout(() => {
    tapCount.value = 0;
  }, TAP_MAX_INTERVAL_MS);
};

let safetyTimeout = null;
// Primeira montagem por contexto JS = cold boot: descarta flag de tentativa
// anterior (processo pode ter morrido no meio do fluxo e a flag ficou órfã,
// deixando o botão preso em "Connecting...").
let coldBootHandled = false;

const checkAndResumeAuth = async () => {
  if (userStore.state.userInfo && (userStore.state.userInfo.id || userStore.state.userInfo.email || userStore.state.userInfo.name)) {
    sessionStorage.removeItem('triad_auth_in_progress');
    router.replace('/');
    return;
  }

  const authInProgress = sessionStorage.getItem('triad_auth_in_progress') === 'true';
  const hasSessionId = !!localStorage.getItem('triad_session_id');

  if (hasSessionId) {
    loading.value = true;
    try {
      import('@capacitor/browser').then(({ Browser }) => Browser.close().catch(() => {})).catch(() => {});
      await withTimeout(userStore.dispatch('info', true), 12000);
      if (userStore.state.userInfo && (userStore.state.userInfo.id || userStore.state.userInfo.email || userStore.state.userInfo.name)) {
        sessionStorage.removeItem('triad_auth_in_progress');
        router.replace('/');
        return;
      }
    } catch (e) {
      // 401 (sessão inválida) não é erro transitório: limpa e libera a UI
      // em vez de deixar o spinner para sempre. TIMEOUT: só libera a UI, a
      // sessão pode ainda ser válida (dispatch segue em background).
      if (e && e.code === 'UNAUTHORIZED') {
        sessionStorage.removeItem('triad_auth_in_progress');
        localStorage.removeItem('triad_session_id');
      }
      if (e && e.code === 'TIMEOUT') {
        sessionStorage.removeItem('triad_auth_in_progress');
      }
      console.warn('Auth check on resume:', e);
    } finally {
      loading.value = false;
    }
  } else if (authInProgress) {
    // Auth em progresso no navegador, mas deep link ainda não entregou o session_id.
    // Mantém spinner ativo e aguarda o deep link (appUrlOpen / app:auth-resumed).
    // NÃO dispara info() aqui sem sid para evitar 401 prematuro que cancelaria o login.
    loading.value = true;
  } else {
    // Nem sessão, nem login em progresso: garante UI liberada
    loading.value = false;
  }
};

const handleLogin = async () => {
  loading.value = true;
  try {
    await clearAllUserCaches();
  } catch (err) {
    console.warn('[LoginView] Cache clear warning:', err);
  }
  sessionStorage.setItem('triad_auth_in_progress', 'true');

  if (safetyTimeout) clearTimeout(safetyTimeout);
  safetyTimeout = setTimeout(() => {
    loading.value = false;
    sessionStorage.removeItem('triad_auth_in_progress');
  }, 35000);

  // Força abertura direta da tela de login (dev-login) ignorando SSO zumbi
  const localeQuery = currentLanguage.value ? `locale=${encodeURIComponent(currentLanguage.value)}` : '';
  const promptQuery = 'prompt=login';
  const queryString = [promptQuery, localeQuery].filter(Boolean).join('&');
  const loginPath = `/login?${queryString}`;
  try {
    await openAuthSession(loginPath);
  } catch (e) {
    console.error('Login trigger error:', e);
    loading.value = false;
    sessionStorage.removeItem('triad_auth_in_progress');
  }
};

watch(() => userStore.state.userInfo, (newVal) => {
  if (newVal && (newVal.id || newVal.email || newVal.name)) {
    sessionStorage.removeItem('triad_auth_in_progress');
    if (safetyTimeout) clearTimeout(safetyTimeout);
    router.replace('/');
  }
}, { immediate: true });

const onAuthResumed = () => {
  checkAndResumeAuth();
};

const onVisibilityChange = () => {
  if (document.visibilityState === 'visible') {
    checkAndResumeAuth();
  }
};

onMounted(async () => {
  appEvents.on('app:auth-resumed', onAuthResumed);
  window.addEventListener('focus', checkAndResumeAuth);
  document.addEventListener('visibilitychange', onVisibilityChange);

  try {
    appVersion.value = await getCurrentAppVersion();
  } catch (e) {
    appVersion.value = '1.1.2';
  }

  if (!coldBootHandled) {
    coldBootHandled = true;
    sessionStorage.removeItem('triad_auth_in_progress');
  }

  await checkAndResumeAuth();
});

onBeforeUnmount(() => {
  appEvents.off('app:auth-resumed', onAuthResumed);
  window.removeEventListener('focus', checkAndResumeAuth);
  document.removeEventListener('visibilitychange', onVisibilityChange);
  if (safetyTimeout) clearTimeout(safetyTimeout);
  if (tapTimer) clearTimeout(tapTimer);
  if (splashTimeout) clearTimeout(splashTimeout);
});
</script>
